import type { UtenteDTO } from '@/models/dto/utente/utente-dto'
import type { UtentiPaginationDTO } from '@/models/dto/utente/utenti-pagination-dto'
import { mapToNuovoUtenteDTO, mapToUser, mapToUtentiDTO } from '@/models/mappers/user/user-mapper'
import { mapToUsersWithPagination } from '@/models/mappers/user/users-mapper'
import type { User } from '@/models/types/user/user'
import type { UsersWithPagination } from '@/models/types/user/users-with-pagination'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import { makeRequest, type MakeRequestOptions } from './make-request'

type UserParams = {
    order?: OrderType
    sort?: keyof UtenteDTO
    search?: string
    page?: string
} & Partial<Record<keyof User, string>>

type UserCrudType = () => {
    getOne: (id: number, options?: MakeRequestOptions) => Promise<User>
    getMany: (
        itemsPerPage: string,
        params?: UserParams,
        onlyEnabled?: boolean,
        options?: MakeRequestOptions,
    ) => Promise<UsersWithPagination>
    getUniqueByEmail: (email: string, options?: MakeRequestOptions) => Promise<User>
    post: (user: Omit<User, 'id'>, options?: MakeRequestOptions) => Promise<User>
    put: (user: User, options?: MakeRequestOptions) => Promise<User>
    delete: (id: number, options?: MakeRequestOptions) => Promise<void>
}

const USER_URL =
    typeof window === 'undefined'
        ? `${process.env.API_URL}/api/v1/Utenti`
        : `${location.origin}${process.env.NEXT_PUBLIC_BASEPATH}/api/v1/Utenti`
const EMAIL_URL =
    typeof window === 'undefined'
        ? `${process.env.API_URL.replace('/api', '')}/email`
        : `${location.origin}${process.env.NEXT_PUBLIC_BASEPATH}/email`
const USER_TAG = 'user'

const userCRUD: UserCrudType = () => ({
    getOne: async (id, options) => {
        const url = new URL(`${USER_URL}/${id.toString()}`)
        const utenteDTO = await makeRequest<UtenteDTO>(url, {
            method: 'GET',
            next: { tags: [USER_TAG] },
            ...options,
        })
        const user = mapToUser(utenteDTO)
        return user
    },
    getMany: async (itemsPerPage, params, onlyEnabled = false, options) => {
        const url = new URL(USER_URL)
        url.searchParams.append('PageSize', itemsPerPage)
        if (params?.page) url.searchParams.append('PageIndex', params.page)
        if (params?.order) url.searchParams.append('Descending', params.order === 'desc' ? 'true' : 'false')
        if (params?.sort) url.searchParams.append('SortBy', capitalizeFirstLetter(params.sort))

        if (onlyEnabled) url.searchParams.append('OnlyActive', 'true')

        if (params?.search) url.searchParams.append('Filters[search]', params.search)

        if (params?.firstName) url.searchParams.append('Filters[Nome]', params.firstName)
        if (params?.lastName) url.searchParams.append('Filters[Cognome]', params.lastName)
        if (params?.email) url.searchParams.append('Filters[Email]', params.email)
        if (params?.company) url.searchParams.append('Filters[Azienda]', params.company)
        if (params?.roleName) url.searchParams.append('Filters[Ruolo]', params.roleName)

        const utentiPaginationDTO = await makeRequest<UtentiPaginationDTO>(url, {
            method: 'GET',
            next: { tags: [USER_TAG] },
            ...options,
        })
        const users = mapToUsersWithPagination(utentiPaginationDTO)
        return users
    },
    getUniqueByEmail: async (email, options) => {
        const url = new URL(`${EMAIL_URL}/${email}`)
        const utenteDTO = await makeRequest<UtenteDTO>(url, {
            method: 'GET',
            next: { tags: [USER_TAG] },
            ...options,
        })
        const user = mapToUser(utenteDTO)
        return user
    },
    post: async (user, options) => {
        const newUtentiDTO = mapToNuovoUtenteDTO(user)
        const url = new URL(USER_URL)
        const utenteDTO = await makeRequest<UtenteDTO>(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newUtentiDTO),
            ...options,
        })
        const newUser = mapToUser(utenteDTO)
        return newUser
    },
    put: async (user, options) => {
        const utentiDTO = mapToUtentiDTO(user)
        const url = new URL(USER_URL)
        const utenteDTO = await makeRequest<UtenteDTO>(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(utentiDTO),
            ...options,
        })
        const newUser = mapToUser(utenteDTO)
        return newUser
    },
    delete: async (id, options) => {
        const url = new URL(`${USER_URL}/${id.toString()}`)
        await makeRequest(url, { method: 'DELETE', ...options })
    },
})

export { USER_TAG, userCRUD, type UserCrudType, type UserParams as UserGetOptions }
